import { z } from 'zod';

const EnvSchema = z.object({
  NEXT_PUBLIC_SUPABASE_URL: z.string().url(),
  NEXT_PUBLIC_SUPABASE_ANON_KEY: z.string().min(1),
  NEXT_PUBLIC_APP_URL: z.string().url().default('http://localhost:3100'),
  // Host Nango Cloud (default https://api.nango.dev). Public car embarqué dans
  // le SDK frontend @nangohq/frontend via `Nango.create({ host })`. Pas un
  // secret — c'est juste un endpoint API. Cf. docs/knowledge-os.md §C1.1.
  NEXT_PUBLIC_NANGO_HOST: z.string().url().default('https://api.nango.dev'),
});

export type Env = z.infer<typeof EnvSchema>;

// Variables server-only — jamais exposées au browser, jamais inlinées dans le
// bundle client. Lues à la demande via `getServerEnv()`. La résolution est
// "best-effort" : un champ optionnel manquant ne fait pas crasher le boot,
// l'appelant doit gérer l'absence (ex: désactiver l'envoi d'email).
const ServerEnvSchema = z.object({
  BREVO_API_KEY: z.string().min(1).optional(),
  SMTP_ADMIN_EMAIL: z.string().email().optional(),
  SMTP_SENDER_NAME: z.string().min(1).optional(),
  // URL racine de n8n côté Docker network interne (ex. http://n8n:5678) — pas
  // d'`/webhook` final ici, c'est le caller qui ajoute `/webhook/<path>`.
  // Injectée par le compose frontend (env_file: /srv/data/dev/.env).
  // Optional pour ne pas casser le boot quand un consumer ne l'utilise pas ;
  // l'appelant (ex. /oauth/callback) doit échouer explicitement si absent.
  N8N_WEBHOOK_URL: z.string().url().optional(),
  // OVH Object Storage S3 — server-only. Utilisé par lib/storage/.
  // Les 5 vars sont group-required : soit toutes présentes, soit toutes absentes
  // (un consumer qui touche au storage doit échouer explicitement si l'une manque).
  OVH_S3_ENDPOINT: z.string().url().optional(),
  OVH_S3_REGION: z.string().min(1).optional(),
  OVH_S3_BUCKET: z.string().min(1).optional(),
  OVH_S3_ACCESS_KEY: z.string().min(1).optional(),
  OVH_S3_SECRET_KEY: z.string().min(1).optional(),
  // Secret partagé serveur-à-serveur — server-only. Utilisé par les Route
  // Handlers `/api/internal/*` pour vérifier l'auth des appels venant des
  // workers n8n via le header `Authorization: Bearer <secret>`. Volontairement
  // distinct du service_role Supabase : aucune route Next.js ne doit jamais
  // lire le service_role_key (scope minimal en cas de fuite côté frontend).
  INTERNAL_API_SECRET: z.string().min(1).optional(),
  // Jeton du flux JSON du corpus partagé RING (A3P), lu par la synchro
  // `/api/internal/shared-corpus/sync` d'après `shared_corpus_feeds.token_env`.
  // Absent : la synchro de ce flux répond une erreur explicite.
  RING_FEED_TOKEN: z.string().min(1).optional(),
  // Token partagé Frontend → n8n. Header `Authorization: Bearer <token>` exigé
  // par le credential `MINDEXIA API` côté workflows (RESOURCE - Start,
  // NAMESPACE - Start, etc.). Doit matcher la valeur stockée dans
  // n8n.credentials_entity. Server-only, jamais inliné dans le bundle client.
  MINDEXIA_API_TOKEN: z.string().min(1).optional(),
  // Stripe — server-only. Pris en charge par lib/stripe/client.ts. En test
  // mode (sk_test_*) sur dev, switchera en sk_live_* au cutover prod. Cf.
  // docs/subscriptions-and-pricing.md §10.
  STRIPE_SECRET_KEY: z
    .string()
    .regex(/^sk_(test|live)_/)
    .optional(),
  // Secret de signature des webhooks Stripe. Récupéré du Dashboard Stripe
  // (Developers → Webhooks → endpoint dédié) ou via Stripe CLI en dev local
  // (`stripe listen` affiche un whsec_ éphémère). Le handler /api/stripe/webhook
  // refuse toute requête sans signature valide.
  STRIPE_WEBHOOK_SECRET: z
    .string()
    .regex(/^whsec_/)
    .optional(),
  // Supabase service_role — server-only, bypass RLS. À utiliser uniquement
  // pour les Route Handlers webhook (sans session utilisateur) ou les
  // crons internes. Jamais lu depuis un Client Component, jamais inliné
  // dans un bundle public. Cf. lib/supabase/admin.ts.
  SUPABASE_SERVICE_ROLE_KEY: z.string().min(1).optional(),
  // URL interne du gateway Supabase (kong) sur le réseau Docker — ex.
  // http://kong:8000. Utilisée par le client admin server-to-server pour NE PAS
  // sortir sur l'edge public Cloudflare (sinon rate-limit 1015 sous rafale de
  // jobs, cf. incident brouillons email 2026-06-18). Optionnelle : fallback sur
  // l'URL publique NEXT_PUBLIC_SUPABASE_URL si absente. Cf. CLAUDE.md §3.3/§8.
  KONG_URL_INTERNAL: z.string().url().optional(),
  // Secret partagé entre l'agent gateway (Next.js) et les workflows tool
  // n8n. Signe en HMAC SHA-256 les enveloppes ToolCall pour garantir
  // authenticité + intégrité du payload (user_id, scope, input) et
  // empêcher l'appel direct du webhook tool depuis l'extérieur du
  // réseau de confiance. Server-only. Cf. docs/agentic-architecture.md §16.
  MINDEXIA_TOOL_SECRET: z.string().min(32).optional(),
  // Secret de signature des JWT "acting_as" émis par l'agent gateway pour
  // les jobs internes (email auto-reply worker, conversation ingestion).
  // Distinct du JWT Supabase car ces tokens représentent un service
  // principal agissant au nom d'un user, pas une session humaine. Court
  // (15 min) + claim acting_as audité dans agent_audit_log.
  MINDEXIA_AGENT_JWT_SECRET: z.string().min(32).optional(),
  // Clés des providers LLM utilisés par les tools natifs côté Next.js
  // (lib/agent/tools/*). Server-only. La table `public.model` (alias →
  // provider) est source de vérité pour le routing.
  //
  // Conventions Mindexia (sovereign-first) :
  //  - MISTRAL_API_KEY : modèles Mistral (Paris). Utilisé pour
  //    `llm_answering` (mistral-large-2512) et `llm_fast` (mistral-small).
  //  - OVH_AI_ENDPOINTS_KEY + URL : modèles open-source hébergés par OVHcloud
  //    (Llama, Qwen, Mistral, gpt-oss). API compatible OpenAI.
  //  - SCW_SECRET_KEY : Generative APIs Scaleway (Paris, Zero Data Retention).
  //    API compatible OpenAI, mêmes poids open-source qu'OVH mais slugs en
  //    minuscules et grille tarifaire distincte. C'est la *secret key* IAM qui
  //    sert de bearer (permission `GenerativeApisModelAccess` suffit) — les
  //    autres variables `SCW_*` de la CLI Scaleway (`SCW_ACCESS_KEY`,
  //    `SCW_DEFAULT_ORGANIZATION_ID`) ne servent pas aux Generative APIs.
  //    `SCW_DEFAULT_PROJECT_ID`, lui, scope la facturation par Projet en
  //    s'insérant dans l'URL. Cf. docs/ovh-ai-endpoints.md § Scaleway.
  MISTRAL_API_KEY: z.string().min(1).optional(),
  OVH_AI_ENDPOINTS_KEY: z.string().min(1).optional(),
  OVH_AI_ENDPOINTS_URL: z.string().url().optional(),
  SCW_SECRET_KEY: z.string().min(1).optional(),
  SCW_DEFAULT_PROJECT_ID: z.string().min(1).optional(),
  // OPENAI_API_KEY : HORS pool souverain. Présente uniquement pour les bake-off
  // exploratoires (`frontend/tests/eval/*.eval.test.ts`, gatés par variable
  // d'env). Aucun alias de `public.model` ni aucune chaîne de fallback ne doit
  // pointer vers ce provider : un chemin actif reste OVH / Mistral / Scaleway.
  OPENAI_API_KEY: z.string().min(1).optional(),
  // GEMINI_API_KEY : HORS pool souverain, même statut que la clé OpenAI (bake-off exploratoires
  // seulement, endpoint compatible OpenAI de l'API Gemini). Jamais d'alias ni de chaîne.
  GEMINI_API_KEY: z.string().min(1).optional(),
  // OPENROUTER_API_KEY : HORS pool souverain, même statut que les deux clés ci-dessus. Sert
  // uniquement au client du modèle de décision Jev (`agent/llm-decision.ts`) dans les bake-off
  // exploratoires. Jamais d'alias ni de chaîne.
  OPENROUTER_API_KEY: z.string().min(1).optional(),
  // Override explicite (déploiement dédié `https://<uuid>.ifr.fr-par.scaleway.com/v1`).
  // Vide → l'URL est dérivée du projet, cf. `scalewayBaseUrl()` dans agent/llm.ts.
  SCW_AI_ENDPOINTS_URL: z.string().url().optional(),
  // Nango Cloud — secret key pour appels SDK @nangohq/node depuis nos Route
  // Handlers + sub-workflow n8n. Server-only, jamais bundle client.
  // Defense-in-depth obligatoire : tout call SDK vérifie le client_id du
  // caller avant. Cf. skill mindexia-connector §20.
  NANGO_SECRET_KEY: z.string().min(1).optional(),
  // Secret de signature des webhooks Nango (HMAC-SHA256). Récupéré dans
  // Nango dashboard → Settings → Webhooks. Vérifié dans /api/connectors/nango/webhook
  // via `verifyWebhookSignature()` — rejet 401 immédiat si invalide.
  NANGO_WEBHOOK_SIGNING_SECRET: z.string().min(1).optional(),
  // URL interne du micro-service file-services (conversion PDF/office, resize
  // images) sur le réseau Docker. Le nom de service est identique sur toutes
  // les instances (template compose factory) — le défaut suffit partout.
  FILE_SERVICE_URL_INTERNAL: z.string().url().default('http://file-services:5001'),
  // Relais LLM d'instance (`http://llm-relay:8080`, posé par le compose factory). Présent,
  // tout appel Mistral et OVH passe par lui avec INTERNAL_API_SECRET et Next.js n'utilise
  // plus MISTRAL_API_KEY ni OVH_AI_ENDPOINTS_KEY. Absent : appel direct (tests, CI, retour
  // arrière). Cf. lib/llm-relay/endpoints.ts et docs/llm-client-keys-plan.md.
  LLM_RELAY_URL: z.string().url().optional(),
  // Kill-switchs runtime email intelligence ('1' = coupé). Freins d'urgence
  // sans toucher n8n ni la DB. Cf. docs/email-answering.md.
  EMAIL_DRAFT_AUTO_DISABLED: z.string().optional(),
  EMAIL_UNION_SCOPE_DISABLED: z.string().optional(),
  EMAIL_DRAFT_VERIFY_DISABLED: z.string().optional(),
  /**
   * Garde d'engagements : 'signal' par DÉFAUT (flags et escalade, aucune réécriture).
   * 'rewrite' rétablit l'ancien comportement — mesuré 0 amélioration sur 32.
   */
  EMAIL_COMMITMENT_GUARD_MODE: z.string().optional(),
  /**
   * Révision de la passe de vérification : désactivée par défaut (les issues restent
   * signalées). 'on' la rétablit — mesurée 15 dégradations pour 5 améliorations.
   */
  EMAIL_VERIFY_REVISION: z.string().optional(),
  EMAIL_AGENTIC_SEARCH_DISABLED: z.string().optional(),
  EMAIL_RESPONSE_PLAN_DISABLED: z.string().optional(),
  EMAIL_PLAYBOOK_DISABLED: z.string().optional(),
  EMAIL_LEARNING_DISABLED: z.string().optional(),
});

export type ServerEnv = z.infer<typeof ServerEnvSchema>;

function readEnv(): Env {
  const parsed = EnvSchema.safeParse({
    NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL,
    NEXT_PUBLIC_NANGO_HOST: process.env.NEXT_PUBLIC_NANGO_HOST,
  });

  if (!parsed.success) {
    const missing = parsed.error.issues.map((i) => i.path.join('.')).join(', ');
    throw new Error(
      `Invalid or missing environment variables: ${missing}. ` +
        `Check .env.local against .env.example.`,
    );
  }

  return parsed.data;
}

// Cache stocké sur `globalThis` plutôt qu'en variable de module : Vitest peut
// charger `@/lib/env` plusieurs fois (jsdom + server-only shim, aliases) et
// chaque instance aurait alors son propre cache désynchronisé. Cf. incident
// 2026-05-11 où `getServerEnv()` retournait une valeur stale dans certains
// tests même après normalisation des env vars dans `tests/agent/db.ts`.
type EnvCache = { client: Env | null; server: ServerEnv | null };
const GLOBAL_KEY = '__mindexia_env_cache_v1';
function cacheStore(): EnvCache {
  const g = globalThis as unknown as Record<string, EnvCache | undefined>;
  if (!g[GLOBAL_KEY]) g[GLOBAL_KEY] = { client: null, server: null };
  return g[GLOBAL_KEY]!;
}

export function getEnv(): Env {
  const store = cacheStore();
  if (!store.client) {
    store.client = readEnv();
  }
  return store.client;
}

// Une string vide dans `.env` (ex. `MISTRAL_API_KEY=`) devient `""` côté Node,
// pas `undefined`. Or `z.string().regex().optional()` accepte undefined mais
// pas "". Sans normalisation, un seul `KEY=` dans `.env` faisait fail le
// safeParse entier et cachait `{}` — toutes les vars devenaient invisibles.
function emptyToUndefined(v: string | undefined): string | undefined {
  return v === '' ? undefined : v;
}

export function getServerEnv(): ServerEnv {
  const store = cacheStore();
  if (!store.server) {
    const raw = {
      BREVO_API_KEY: emptyToUndefined(process.env.BREVO_API_KEY),
      SMTP_ADMIN_EMAIL: emptyToUndefined(process.env.SMTP_ADMIN_EMAIL),
      SMTP_SENDER_NAME: emptyToUndefined(process.env.SMTP_SENDER_NAME),
      N8N_WEBHOOK_URL: emptyToUndefined(process.env.N8N_WEBHOOK_URL),
      OVH_S3_ENDPOINT: emptyToUndefined(process.env.OVH_S3_ENDPOINT),
      OVH_S3_REGION: emptyToUndefined(process.env.OVH_S3_REGION),
      OVH_S3_BUCKET: emptyToUndefined(process.env.OVH_S3_BUCKET),
      OVH_S3_ACCESS_KEY: emptyToUndefined(process.env.OVH_S3_ACCESS_KEY),
      OVH_S3_SECRET_KEY: emptyToUndefined(process.env.OVH_S3_SECRET_KEY),
      INTERNAL_API_SECRET: emptyToUndefined(process.env.INTERNAL_API_SECRET),
      RING_FEED_TOKEN: emptyToUndefined(process.env.RING_FEED_TOKEN),
      MINDEXIA_API_TOKEN: emptyToUndefined(process.env.MINDEXIA_API_TOKEN),
      STRIPE_SECRET_KEY: emptyToUndefined(process.env.STRIPE_SECRET_KEY),
      STRIPE_WEBHOOK_SECRET: emptyToUndefined(process.env.STRIPE_WEBHOOK_SECRET),
      SUPABASE_SERVICE_ROLE_KEY: emptyToUndefined(process.env.SUPABASE_SERVICE_ROLE_KEY),
      KONG_URL_INTERNAL: emptyToUndefined(process.env.KONG_URL_INTERNAL),
      MINDEXIA_TOOL_SECRET: emptyToUndefined(process.env.MINDEXIA_TOOL_SECRET),
      MINDEXIA_AGENT_JWT_SECRET: emptyToUndefined(process.env.MINDEXIA_AGENT_JWT_SECRET),
      MISTRAL_API_KEY: emptyToUndefined(process.env.MISTRAL_API_KEY),
      OVH_AI_ENDPOINTS_KEY: emptyToUndefined(process.env.OVH_AI_ENDPOINTS_KEY),
      OVH_AI_ENDPOINTS_URL: emptyToUndefined(process.env.OVH_AI_ENDPOINTS_URL),
      SCW_SECRET_KEY: emptyToUndefined(process.env.SCW_SECRET_KEY),
      SCW_DEFAULT_PROJECT_ID: emptyToUndefined(process.env.SCW_DEFAULT_PROJECT_ID),
      OPENAI_API_KEY: emptyToUndefined(process.env.OPENAI_API_KEY),
      GEMINI_API_KEY: emptyToUndefined(process.env.GEMINI_API_KEY),
      OPENROUTER_API_KEY: emptyToUndefined(process.env.OPENROUTER_API_KEY),
      SCW_AI_ENDPOINTS_URL: emptyToUndefined(process.env.SCW_AI_ENDPOINTS_URL),
      NANGO_SECRET_KEY: emptyToUndefined(process.env.NANGO_SECRET_KEY),
      NANGO_WEBHOOK_SIGNING_SECRET: emptyToUndefined(process.env.NANGO_WEBHOOK_SIGNING_SECRET),
      FILE_SERVICE_URL_INTERNAL: emptyToUndefined(process.env.FILE_SERVICE_URL_INTERNAL),
      LLM_RELAY_URL: emptyToUndefined(process.env.LLM_RELAY_URL),
      EMAIL_DRAFT_AUTO_DISABLED: emptyToUndefined(process.env.EMAIL_DRAFT_AUTO_DISABLED),
      EMAIL_UNION_SCOPE_DISABLED: emptyToUndefined(process.env.EMAIL_UNION_SCOPE_DISABLED),
      EMAIL_DRAFT_VERIFY_DISABLED: emptyToUndefined(process.env.EMAIL_DRAFT_VERIFY_DISABLED),
      EMAIL_COMMITMENT_GUARD_MODE: emptyToUndefined(process.env.EMAIL_COMMITMENT_GUARD_MODE),
      EMAIL_VERIFY_REVISION: emptyToUndefined(process.env.EMAIL_VERIFY_REVISION),
      EMAIL_AGENTIC_SEARCH_DISABLED: emptyToUndefined(process.env.EMAIL_AGENTIC_SEARCH_DISABLED),
      EMAIL_RESPONSE_PLAN_DISABLED: emptyToUndefined(process.env.EMAIL_RESPONSE_PLAN_DISABLED),
      EMAIL_PLAYBOOK_DISABLED: emptyToUndefined(process.env.EMAIL_PLAYBOOK_DISABLED),
      EMAIL_LEARNING_DISABLED: emptyToUndefined(process.env.EMAIL_LEARNING_DISABLED),
    };
    const parsed = ServerEnvSchema.safeParse(raw);
    if (parsed.success) {
      store.server = parsed.data;
    } else {
      // Une var PRÉSENTE mais malformée (préfixe Stripe, URL invalide…) est une
      // erreur de configuration d'instance — jamais silencieuse. L'ancien
      // comportement (`{}` muet) éteignait storage/LLM/internal-API d'un coup
      // sans aucune trace serveur.
      const issues = parsed.error.issues.map(
        (i) => `${i.path.join('.') || '(root)'}: ${i.message}`,
      );
      console.error('[env] Invalid server environment variable(s):', issues.join(' ; '));
      if (process.env.NODE_ENV === 'production') {
        throw new Error(
          `Invalid server environment variable(s): ${parsed.error.issues
            .map((i) => i.path.join('.'))
            .join(', ')} — fix the instance .env (see .env.example).`,
        );
      }
      // Hors prod : on écarte uniquement les clés invalides, le reste survit.
      const invalid = new Set(parsed.error.issues.map((i) => String(i.path[0])));
      const filtered = Object.fromEntries(Object.entries(raw).filter(([key]) => !invalid.has(key)));
      const retry = ServerEnvSchema.safeParse(filtered);
      store.server = retry.success ? retry.data : ServerEnvSchema.parse({});
    }
  }
  return store.server;
}

// Reset des caches mémoïsés — usage tests uniquement, ne pas appeler en runtime.
export function __resetEnvCacheForTests(): void {
  const store = cacheStore();
  store.client = null;
  store.server = null;
}
